import Bugsnag from '@bugsnag/js';
import { useFeatureFlagEnabled } from '@posthog/react';
import { useLocation, useParams } from '@tanstack/react-router';
import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import * as MapGL from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import { addToken, addTokenToImageValue } from '~/api/layers2';
import { useQuery } from '~/common/routerUtils';
import { CG_ORG_ID, CG_PROJ_ID, INTELLIGENCE_ROUTE, type OrganizationDetail, type TLoadedState } from '~/common/types';
import { useOutOfRegion } from '~/hooks/useCheckRegionBounds';
import type { ProjectDetailsParams } from '~/pages/projectDetails';
import { intelligenceLayers } from '~/pages/projectDetails/intelligence/common/config';
import { CUSTOM_DATA_LAYER_PREFIX } from '~/pages/projectDetails/map/customDataLayers.helpers';
import { GEOREFERENCED_MAP_LAYER_PREFIX } from '~/pages/projectDetails/map/georeferencedMapLayers.helpers';
import rootStore from '~/store';
import store from '~/store';
import { digitizationLayerIds } from '~/store2/digitizationLayers';
import layerConfigStore from '~/store2/layerConfig';
import layerStore, { type MapboxSourceConfig, type VectorLayer } from '~/store2/layers';
import { filterProtectedLayerIds, isAwaitingProtectedCatalog } from '~/store2/protectedLayers';
import {
  FIBER_LIT_BUILDINGS_LAYER_ID,
  isProjectScopedRendererLayer,
  shouldRegisterFiberLitBuildingsLayer,
} from './activeLayerIds';
import { resolvePromoteId } from './featureStateHighlight.helpers';
import { HIGHLIGHT_LINE_LAYER_ID } from './HighlightLayer';
import { resolveAllowSymbolOverlap } from './symbolOverlap.helpers';

// duplicated in packages/jobs/src/processors/drf/common.ts
export const SIBLING_AGENCY_MAP: { [agencyId: string]: string[] } = {
  '43': ['43', '8'],
  '8': ['8', '43'],
  '1000004': ['1000004', '1000005', '1000081'],
  '1000005': ['1000004', '1000005', '1000081'],
  '1000081': ['1000004', '1000005', '1000081'],
};

type OpacityProperty = 'raster-opacity' | 'line-opacity' | 'fill-opacity';

function isDigitizedLayer(layerId: string): boolean {
  return digitizationLayerIds.includes(layerId);
}

/**
 * README - getProjectFilters
 * 'any' - match any of the conditions in the filter
 *
 * --- Case 1 ---
 * cg_projID property exists on the feature, it must match the projectId param
 *
 * --- Case 2 ---
 * cg_projID does not exist, see if the cg_OrgID property matches the currentOrganizationId.
 */

function getProjectFilters(orgId: string, projectId: string): any[] {
  return [
    'any',
    // Case 1
    ['all', ['has', CG_PROJ_ID], ['==', CG_PROJ_ID, projectId]],
    // Case 2
    ['all', ['!has', CG_PROJ_ID], ['has', CG_ORG_ID], ['==', CG_ORG_ID, orgId]],
  ];
}

/**
 * README - getOrgFilters
 * NOTE - At this point, the cg_projID property does not exist
 * 'all' - all following conditions must be satisfied to pass the filter check
 *
 * --- Case 1 ---
 * cg_projID must NOT exist
 *
 * --- Case 2 ---
 * cg_OrgID must match the currentOrganizationId
 */

function getOrgFilters(orgId: string): any[] {
  return [
    'all',
    // Case 1
    ['!has', CG_PROJ_ID],
    // Case 2
    ['all', ['has', CG_ORG_ID], ['==', CG_ORG_ID, orgId]],
  ];
}

/**
 * README - filterAll
 * Filters can NOT be conditional in react-map-gl
 * If (for some reason) no orgId AND no projectId exist
 * then give it a condition that will ALWAYS fail the filter check
 * In this case, a cg_projID of 'zzz' will always fail (invalid db ID)
 */

function filterAll() {
  return ['==', CG_PROJ_ID, 'zzz'];
}

export default function MapLayers({ projectId: projectIdProp }: { projectId?: string }) {
  const isOutOfRegion = useOutOfRegion();
  const { tabId, projectId: projectIdParam } = useParams({ strict: false }) as Partial<ProjectDetailsParams>;
  const { currentOrganizationId, currentOrganizationDetail } = useSnapshot(store);
  const { tilesConfig, visibleLayerIds, visibleMetadataIds } = useSnapshot(layerStore) as typeof layerStore;
  const [isHighlightLayerLoaded, setHighlightLayerLoaded] = useState(false);
  const queryString = useQuery();
  const { inUnverifiedMode } = useSnapshot(rootStore);
  // Reactive dependency for the unverified-mode filter below: the protected-leaf set is
  // derived from the catalog, which can arrive after this component first renders.
  const { catalogVersion } = useSnapshot(layerConfigStore);
  const { current: map } = MapGL.useMap();
  const { pathname } = useLocation();
  const shouldAllowSymbolOverlapPointLayers =
    useFeatureFlagEnabled('always-allow-symbol-overlap-point-layers') === true;

  const projectId = projectIdProp || projectIdParam;

  useEffect(() => {
    if (!map) return;

    const checkHighlightLayer = () => {
      const layerExists = !!map.getLayer(HIGHLIGHT_LINE_LAYER_ID);
      setHighlightLayerLoaded(layerExists);
    };

    checkHighlightLayer();

    map.on('styledata', checkHighlightLayer);

    return () => {
      map.off('styledata', checkHighlightLayer);
    };
  }, [map]);

  const getCustomOpacity = (layerId: string, property: OpacityProperty) => {
    const props: { [key: string]: string | number | boolean } = {};

    if (tabId === INTELLIGENCE_ROUTE && intelligenceLayers.includes(layerId)) {
      props[property] = 0;
    }

    return props;
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  const layersOnMap = useMemo(() => {
    if (!tilesConfig) {
      return [];
    }

    if (!isHighlightLayerLoaded) {
      return [];
    }

    const _sources: ReactNode[] = [];
    const _layerIds: string[] = [];

    const addLayer = (layerId: string) => {
      // Project-scoped dedicated renderers (FiberLitBuildingsLayer, FieldNotesLayer,
      // PotholesLayer) have no tilesConfig tile source — skip so we don't false-alarm
      // "Missing config". See PROJECT_SCOPED_RENDERER_LAYER_IDS in activeLayerIds.ts.
      if (isProjectScopedRendererLayer(layerId)) return null;

      // Custom-data WMTS rasters render via the dedicated CustomDataLayers component
      // (project-scoped, not in tilesConfig); skip so we don't log a missing-config error.
      if (layerId.startsWith(CUSTOM_DATA_LAYER_PREFIX)) return null;
      // Geo-referenced map PNGs are drawn by GeoreferencedMapLayers, not from tilesConfig.
      if (layerId.startsWith(GEOREFERENCED_MAP_LAYER_PREFIX)) return null;

      const config = tilesConfig[layerId];

      if (!config) {
        Bugsnag.notify(new Error(`MapLayers.tsx: Missing config for layerId - ${layerId}`));

        return null;
      }

      const getSource = (source: MapboxSourceConfig) => {
        if (source.type === 'vector') {
          // Spread rather than pass directly: mapbox validates the source spec and rejects an
          // explicit `promoteId: undefined` ('string, expression or object expected, "undefined"
          // found'), which layers with no promotable id field would otherwise hit on every render.
          const promoteId = resolvePromoteId(config);
          return (
            <MapGL.Source
              key={layerId}
              type={'vector'}
              id={layerId}
              tiles={config.tiles.map((url) => addToken(url))}
              minzoom={config.minZoom}
              maxzoom={config.maxZoom}
              {...(promoteId ? { promoteId } : {})}
            >
              {source.layers.map(getVectorLayer)}
            </MapGL.Source>
          );
        } else if (source.type === 'raster') {
          return (
            <MapGL.Source
              key={layerId}
              type={'raster'}
              id={layerId}
              tiles={config.tiles.map((url) => addToken(url))}
              minzoom={config.minZoom}
              maxzoom={config.maxZoom}
            >
              <MapGL.Layer
                key={`${layerId}`}
                id={`${layerId}`}
                type="raster"
                source={layerId}
                paint={{
                  'raster-opacity': 0.9,
                  ...getCustomOpacity(layerId, 'raster-opacity'),
                  'raster-emissive-strength': 1,
                }} // TODO: Should this be configurable?
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
            </MapGL.Source>
          );
        } else {
          return null;
        }
      };

      const getVectorLayer = (layer: VectorLayer) => {
        const key = `${layerId}-${layer.id}`;
        _layerIds.push(key);

        const filter: any[] = ['all'];

        // add default layer filters if applicable
        if (layer.filter) {
          filter.push(layer.filter);
        }

        const o = currentOrganizationDetail as TLoadedState & OrganizationDetail;
        const orgAgencyId = o?.orgAgencyId;

        // used in headless mode
        if (queryString.has('civilgridId')) {
          filter.push(['==', 'civilgrid_id', queryString.get('civilgridId')]);
        }
        // if the agency has an orgAgencyId, and the pathname includes '/dataRequestFulfillment',
        // filter out all layers except for the sibling agency ids
        else if (!!orgAgencyId && !!SIBLING_AGENCY_MAP[orgAgencyId] && pathname.includes('/dataRequestFulfillment')) {
          const siblingAgencyIds = SIBLING_AGENCY_MAP[orgAgencyId];
          const inFilter = ['in', 'civilgrid_id', ...siblingAgencyIds];
          filter.push(inFilter);
        }

        // check for digitization, see READMEs above for filter descriptions.
        if (isDigitizedLayer(layerId)) {
          if (projectId) {
            filter.push(getProjectFilters(currentOrganizationId, projectId));
          } else if (currentOrganizationId) {
            filter.push(getOrgFilters(currentOrganizationId));
          } else {
            filter.push(filterAll());
          }
        }

        const visibleProps = Array.from(visibleMetadataIds)
          .filter((p) => p.startsWith(layerId))
          .map((p) => p.split('.')[1]);
        let labels: any[] = [];
        if (visibleProps) {
          labels = ['concat'];
          visibleProps.forEach((p, i) => {
            // if not the first index
            if (i !== 0) {
              // grab the previous property name
              const prev = visibleProps[i - 1];

              // if the previous property name exists (double check)
              if (prev) {
                // if the previous property is "Not Available" (meaning its an empty string)
                // then don't render the dash separator
                // if the previous property DOES EXIST and is NOT "Not Available",
                // then render the separator dash
                labels.push(['case', ['==', ['to-string', ['get', p]], 'Not Available'], '', ' - ']);
              }
            }

            labels.push(['case', ['==', ['to-string', ['get', p]], 'Not Available'], '', ['to-string', ['get', p]]]);

            // 👇 use this to display field name
            // labels.push(`${i !== 0 ? '\n' : ''}${config.fields[p].alias}: `, [
            //   'get',
            //   p,
            // ]);
          });
        }

        if (layer.type === 'line') {
          return [
            <MapGL.Layer
              key={key}
              id={key}
              type="line"
              source={layerId}
              source-layer="all"
              filter={filter}
              layout={{
                'line-cap': 'round',
                'line-join': 'round',
              }}
              paint={{
                ...layer.paint,
                ...getCustomOpacity(layerId, 'line-opacity'),
                'line-emissive-strength': 1,
              }}
              beforeId={HIGHLIGHT_LINE_LAYER_ID}
            />,
            visibleProps.length > 0 && (
              <MapGL.Layer
                key={`${key}-line-label`}
                id={`${key}line-label`}
                type="symbol"
                source={layerId}
                source-layer="all"
                filter={filter}
                layout={{
                  'symbol-placement': 'line',
                  // @ts-expect-error
                  'text-field': labels,
                  'text-offset': [0, -1],
                  'text-size': 14,
                  'text-justify': 'auto',
                }}
                paint={{
                  'text-color': layer.paint['line-color'],
                  'text-halo-color': 'white',
                  'text-halo-width': 0.25,
                  'icon-emissive-strength': 1,
                  'text-emissive-strength': 1,
                }}
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
            ),
          ];
        } else if (layer.type === 'symbol') {
          const allowOverlap = resolveAllowSymbolOverlap(layerId, shouldAllowSymbolOverlapPointLayers);
          return [
            <MapGL.Layer
              key={key}
              id={key}
              type="symbol"
              source={layerId}
              source-layer="all"
              filter={filter}
              paint={layer.paint}
              layout={{
                ...layer.layout,
                'icon-image': addTokenToImageValue(layer.layout['icon-image']),
                ...(allowOverlap
                  ? {
                      'icon-allow-overlap': true,
                      'icon-ignore-placement': true,
                      'text-allow-overlap': true,
                      'text-optional': true,
                    }
                  : {}),
              }}
              beforeId={HIGHLIGHT_LINE_LAYER_ID}
            />,
            visibleProps.length > 0 && (
              <MapGL.Layer
                key={`${key}-symbol-label`}
                id={`${key}symbol-label`}
                type="symbol"
                source={layerId}
                source-layer="all"
                filter={filter}
                layout={{
                  'symbol-placement': 'point',
                  // @ts-expect-error
                  'text-field': labels,
                  'text-offset': [0, Math.max(visibleProps.length, 2) * -1.1],
                  'text-size': 14,
                  'text-justify': 'left',
                  ...(allowOverlap ? { 'text-allow-overlap': true, 'text-ignore-placement': true } : {}),
                }}
                paint={{
                  'text-color': 'black',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 0.25,
                }}
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
            ),
          ];
        } else if (layer.type === 'fill') {
          return [
            <MapGL.Layer
              key={key}
              id={key}
              type="fill"
              source={layerId}
              source-layer="all"
              filter={filter}
              paint={{
                ...layer.paint,
                ...(layer.paint['fill-pattern']
                  ? { 'fill-pattern': addTokenToImageValue(layer.paint['fill-pattern']) }
                  : {}),
                ...getCustomOpacity(layerId, 'fill-opacity'),
              }}
              beforeId={HIGHLIGHT_LINE_LAYER_ID}
            />,
            layer.paint['fill-outline-color'] && (
              <MapGL.Layer
                key={`${key}-outline`}
                id={`${key}-outline`}
                type="line"
                source={layerId}
                source-layer="all"
                filter={filter}
                paint={{
                  'line-width': ['interpolate', ['exponential', 0.5], ['zoom'], 14, 1.5, 20, 3],
                  'line-color': layer.paint['fill-outline-color'],
                  ...getCustomOpacity(layerId, 'line-opacity'),
                }}
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
            ),
            visibleProps.length > 0 && (
              <MapGL.Layer
                key={`${key}-symbol-label`}
                id={`${key}symbol-label`}
                type="symbol"
                source={layerId}
                source-layer="all"
                filter={filter}
                layout={{
                  'symbol-placement': 'point',
                  // @ts-expect-error
                  'text-field': labels,
                  'text-size': 14,
                  'text-justify': 'left',
                }}
                paint={{
                  'text-color': 'black',
                  'text-halo-color': '#ffffff',
                  'text-halo-width': 0.25,
                }}
                beforeId={HIGHLIGHT_LINE_LAYER_ID}
              />
            ),
          ];
        } else if (layer.type === 'circle') {
          return (
            <MapGL.Layer
              key={key}
              id={key}
              type="circle"
              source={layerId}
              source-layer="all"
              filter={filter}
              paint={layer.paint}
              beforeId={HIGHLIGHT_LINE_LAYER_ID}
            />
          );
        }
      };

      // this "if" check helps local dev if new layers are added/removed
      if (config) {
        _sources.push(getSource(config));
      }
    };

    // Never show any utility/digitized/special-dataset layers to unverified users. While the
    // catalog the protected-leaf ids derive from is still expected but absent, hold rather
    // than filter: `isProtectedLeafId` can't answer yet, and `visibleLayerIds` is seeded from
    // localStorage unfiltered, so filtering early can leak a DB-added protected leaf.
    const layersToRender = inUnverifiedMode
      ? isAwaitingProtectedCatalog(catalogVersion)
        ? new Set<string>()
        : filterProtectedLayerIds(visibleLayerIds)
      : visibleLayerIds;
    layersToRender.forEach(addLayer);

    // FiberLitBuildingsLayer renders this separately; register its id so the metadata sidebar can query it.
    if (shouldRegisterFiberLitBuildingsLayer(layersToRender, projectId)) {
      _layerIds.push(FIBER_LIT_BUILDINGS_LAYER_ID);
    }

    layerStore.setActiveLayerIds(_layerIds);

    return _sources;
  }, [
    visibleLayerIds,
    visibleMetadataIds,
    tilesConfig,
    tabId,
    isHighlightLayerLoaded,
    projectId,
    inUnverifiedMode,
    catalogVersion,
    shouldAllowSymbolOverlapPointLayers,
  ]);

  if (isOutOfRegion) {
    return null;
  }

  return <>{layersOnMap}</>;
}
